/**
 * API Response Utilities
 *
 * Shared utilities for handling standard API responses across the frontend.
 * Provides consistent unwrapping of API responses and error handling.
 */

export interface StandardResponse<T = unknown> {
  status: "success" | "error"
  data?: T
  message?: string
  errors?: string[]
  meta?: Record<string, unknown>
}

export interface ErrorResponse {
  status: "error"
  message: string
  errors?: string[]
  code?: string
  details?: Record<string, unknown>
}

/**
 * Unwraps a standard API response and returns the data
 * @param response - The API response object
 * @returns The unwrapped data or null if unsuccessful
 * @throws Error if the response indicates failure
 */
export const unwrapStandardResponse = <T>(response: unknown): T | null => {
  // Guard against HTML responses (e.g., CloudFront SPA error page returning
  // index.html for API 404s instead of JSON)
  if (
    typeof response === "string" &&
    response.trimStart().startsWith("<!DOCTYPE")
  ) {
    throw new Error("Received HTML instead of JSON response")
  }
  if (response && typeof response === "object" && "status" in response) {
    const resp = response as StandardResponse<T>
    if (resp.status === "success") {
      return (resp.data ?? null) as T | null
    }
    const message =
      typeof resp.message === "string" ? resp.message : "Request failed"
    throw new Error(message)
  }
  return response as T
}

/**
 * Unwraps an error response and returns structured error information
 * @param error - The error object from API
 * @returns Structured error response
 */
export const unwrapErrorResponse = (error: unknown): ErrorResponse => {
  // Handle Axios-style error format with response.data
  if (
    error &&
    typeof error === "object" &&
    "response" in error &&
    error.response &&
    typeof error.response === "object" &&
    "data" in error.response
  ) {
    const axiosError = error as {
      response: {
        data: Record<string, unknown>
        status?: number
      }
    }
    const data = axiosError.response.data
    return {
      status: "error",
      message:
        (typeof data.message === "string" ? data.message : undefined) ||
        (typeof data.detail === "string" ? data.detail : undefined) ||
        "An error occurred",
      errors:
        (Array.isArray(data.errors) ? (data.errors as string[]) : undefined) ||
        (typeof data.detail === "string" ? [data.detail] : undefined),
      code:
        (typeof data.code === "string" ? data.code : undefined) ||
        axiosError.response.status?.toString(),
      details:
        data.details && typeof data.details === "object"
          ? (data.details as Record<string, unknown>)
          : undefined,
    }
  }

  // Handle standard error response
  if (
    error &&
    typeof error === "object" &&
    "status" in error &&
    (error as Record<string, unknown>).status === "error"
  ) {
    const errObj = error as ErrorResponse
    return {
      status: "error",
      message: errObj.message || "An error occurred",
      errors: errObj.errors,
      code: errObj.code,
      details: errObj.details,
    }
  }

  // Handle Error object
  if (error instanceof Error) {
    return {
      status: "error",
      message: error.message,
    }
  }

  // Handle string error
  if (typeof error === "string") {
    return {
      status: "error",
      message: error,
    }
  }

  // Fallback
  return {
    status: "error",
    message: "An unknown error occurred",
  }
}

/**
 * Checks if a response is a success response
 */
export const isSuccessResponse = <T>(
  response: unknown,
): response is StandardResponse<T> => {
  return (
    !!response &&
    typeof response === "object" &&
    "status" in response &&
    (response as StandardResponse).status === "success"
  )
}

/**
 * Checks if a response is an error response
 */
export const isErrorResponse = (
  response: unknown,
): response is ErrorResponse => {
  return (
    !!response &&
    typeof response === "object" &&
    "status" in response &&
    (response as StandardResponse).status === "error"
  )
}

/**
 * Safely extracts data from a response, returning null on error
 */
export const safeUnwrap = <T>(response: unknown): T | null => {
  try {
    return unwrapStandardResponse<T>(response)
  } catch {
    return null
  }
}
